// ─────────────────────────────────────────────────────────────
// LukaDuka - Offline Identity (ver1)
// Who is signed in on this phone, read WITHOUT any server call.
//
// ver1 - 2 November 2026 (Offline Capture screen - Layer 2c):
//   - Reads the login Supabase keeps in localStorage and returns ONLY the
//     user ID. Never reads, uses or sends the login token.
//   - Verified 2 Nov 2026 (Claude Code): supabase.ts sets no custom
//     storageKey, so supabase-js uses its default key
//       sb-<project ref>-auth-token
//     where <project ref> is the first part of the Supabase URL's hostname
//     (different for staging and production). The value is JSON with the
//     ID at user.id. The key is worked out at runtime - never hard-coded.
//
// SECURITY: the stored value is not checked by the server at this point,
// so the ID is only used to label and count captures held on this phone.
// It must NEVER be used to authorise anything. When a held capture is sent,
// the server checks the real login (RLS + migration v82).
//
// This is the ONLY file that knows how Supabase stores the login
// (Decision K). A move away from Supabase changes this file only.
//
// Path: src/app/offline/offlineIdentity.ts
// ─────────────────────────────────────────────────────────────

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function storageKey(): string | null {
  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined
  if (!url) return null
  try {
    // Mirrors supabase-js: `sb-${baseUrl.hostname.split(".")[0]}-auth-token`
    const ref = new URL(url).hostname.split('.')[0]
    return ref ? `sb-${ref}-auth-token` : null
  } catch {
    return null
  }
}

/**
 * The signed-in account holder's user ID as stored on this phone, or null
 * if there is no stored login (signed out, or browser data cleared).
 * Synchronous, never touches the network, never throws.
 */
export function getStoredUserId(): string | null {
  try {
    const key = storageKey()
    if (!key || typeof window === 'undefined') return null

    const raw = window.localStorage.getItem(key)
    if (!raw) return null

    const parsed = JSON.parse(raw) as {
      user?: { id?: unknown }
      currentSession?: { user?: { id?: unknown } }  // older supabase-js shape
    }
    const id = parsed?.user?.id ?? parsed?.currentSession?.user?.id
    return typeof id === 'string' && UUID_PATTERN.test(id) ? id : null
  } catch {
    // Storage blocked, or unexpected shape - treat as "no stored login".
    return null
  }
}
